/* eslint-disable no-console */
// Vinculación de un movimiento bancario a 1..N facturas SII, cada factura saldada por sus CUOTAS
// (una o varias) o por un MONTO manual (facturas sin plan). Fuente ÚNICA de escritura del flujo
// "Pago/Cobro SII" del modal (10.1 / 10.103): reemplaza los dos caminos históricos —single con una
// cuota (handleConfirmar) y múltiple sin cuotas (handleConfirmarMultiple)— por una sola lógica que
// no depende de cuántas facturas se eligen.
//
// Modelo de datos (SIN migración de esquema — decisión tomada):
//   - El vínculo AUTORITATIVO cuota↔movimiento es `plan_pagos_facturas.movimiento_bancario_id`
//     (N→1: varias cuotas pueden colgar del mismo movimiento). Lo escribe este util por cada cuota.
//   - `movimientos_bancarios.plan_pago_cuota_id` / `plan_cobro_cuota_id` son ESCALARES y no pueden
//     representar N cuotas: este util NO los escribe nunca (los deja en null). Los motores de cobro
//     (getCobroFactura Camino 1 MBF / getFacturasYaVinculadas Fuente 3) resuelven la cobranza por
//     Σ `movimiento_bancario_facturas.monto`, que este util deja correcto (una fila por factura con
//     el monto APLICADO a esa factura, no el débito entero — fix del bug del puente).
//   - FK escalar de factura (`factura_sii_recibida_id`/`emitida_id`): B3 Fase B — ya NO se escribe (ni
//     single ni multi). La vinculación vive SIEMPRE en la tabla puente MBF; el estado de cobro lo
//     resuelve `getCobroReal` por el puente. (`fkEscalar` en el return sigue indicando "fue single";
//     el caller lo usa solo para el embed OPTIMISTA del badge, no para escribir la FK.)
//
// El cuadre TOTAL (Σ montos aplicados vs |débito|) reusa `evaluarCuadreCuotas` (10.103). Multi-moneda
// por la FECHA del movimiento vía `convertirAMonedaLocal` (10.185). Sin fallo silencioso (10.177):
// cada escritura chequea su `{ error }`. `supabaseClient` se inyecta (default al singleton) para test.

import { evaluarCuadreCuotas } from "./cuadreCuotas";
import { TOLERANCIA_REDONDEO_LOCAL } from "./estadoCuenta";
import { convertirAMonedaLocal } from "./exchangeRateService";
import { client } from "./supabase";

const monedaLocalDe = (pais) => (pais === "Perú" ? "PEN" : "CLP");

// Cuotas tildadas de una factura seleccionada (shape del modal). [] si no hay plan / no se tildó nada.
const cuotasDe = (fSel) => (Array.isArray(fSel?.cuotasSeleccionadas) ? fSel.cuotasSeleccionadas : []);

// Monto aplicado a UNA factura (en la moneda de la factura):
//   - con cuotas tildadas → Σ monto_cuota de las cuotas tildadas (derivado, NO editable).
//   - sin plan → montoAsignado (manual). La UI SIEMPRE lo pre-carga para facturas sin plan.
//   - con plan pero SIN cuotas tildadas → 0 (NUNCA monto_total): así el cuadre no pasa por defecto
//     y no se puede confirmar una factura con plan sin elegir cuotas (evita cuotas huérfanas).
// Es el monto que va a la fila MBF de esa factura (fix del puente: antes iba el débito entero).
export const montoAplicadoFactura = (fSel) => {
  const cuotas = cuotasDe(fSel);
  if (cuotas.length > 0) return cuotas.reduce((s, c) => s + Math.abs(Number(c.monto_cuota || 0)), 0);
  return Number(fSel?.montoAsignado != null ? fSel.montoAsignado : 0);
};

// Moneda de una factura para comparar/convertir: la recibida (pago) siempre es local; la emitida
// (cobro) hereda su propia `moneda` (puede ser USD en DTE 110).
const monedaFacturaDe = (fSel, esCobro, pais) => (esCobro ? fSel?.moneda || monedaLocalDe(pais) : monedaLocalDe(pais));

// Cuadre TOTAL del movimiento contra la Σ de montos aplicados (una entrada por factura), en moneda
// local por la FECHA del movimiento (10.185). Reusa evaluarCuadreCuotas pasando las facturas como
// "conjunto de cuotas" con montoDe = monto aplicado convertido. Tolerancia = redondeo (10) escalado
// por la cantidad de cuotas tildadas (mínimo 1 por factura, como el single).
//   - asignado:   Σ montos aplicados (local).
//   - montoMovCmp: |débito| en local.
//   - sinAsignar: montoMovCmp − asignado, CON signo (>0 falta asignar / <0 sobre-asignado).
//   - cuadra:     |sinAsignar| ≤ tolerancia.
export const evaluarCuadreAplicaciones = ({ facturasSeleccionadas = [], movimiento, esCobro, pais }) => {
  const local = monedaLocalDe(pais);
  const monedaMov = movimiento?.moneda || local;
  const montoMovCmp =
    monedaMov !== local
      ? convertirAMonedaLocal(Math.abs(Number(movimiento?.monto || 0)), monedaMov, pais, movimiento?.fecha)
      : Math.abs(Number(movimiento?.monto || 0));

  const montoDe = (fSel) => {
    const aplicado = montoAplicadoFactura(fSel);
    const monedaFac = monedaFacturaDe(fSel, esCobro, pais);
    return monedaFac !== local ? convertirAMonedaLocal(aplicado, monedaFac, pais, movimiento?.fecha) : aplicado;
  };

  const nCuotas = facturasSeleccionadas.reduce((n, f) => n + Math.max(cuotasDe(f).length, 1), 0);
  const tolerancia = Math.max(nCuotas, 1) * TOLERANCIA_REDONDEO_LOCAL;

  const {
    sumaCuotas: asignado,
    diferencia,
    cuadra,
  } = evaluarCuadreCuotas(facturasSeleccionadas, montoMovCmp, tolerancia, montoDe);
  return { asignado, montoMovCmp, sinAsignar: diferencia, cuadra, moneda: local, tolerancia };
};

// Neto / impuesto de la caja consolidada, prorrateados por el monto aplicado de cada factura
// (montoAplicado / monto_total). Con cuotas parciales el neto no se sobre-declara; con factura
// completa el ratio es 1 y coincide con el total. En moneda cruda de cada factura (la caja hereda
// la moneda del movimiento; el desglose neto/impuesto es informativo).
const netoImpuestoConsolidado = (facturasSeleccionadas) => {
  let neto = 0;
  let impuesto = 0;
  for (const f of facturasSeleccionadas) {
    const total = Number(f.monto_total || 0);
    const ratio = total > 0 ? montoAplicadoFactura(f) / total : 1;
    neto += Number(f.monto_neto || 0) * ratio;
    impuesto += Number(f.monto_iva || 0) * ratio;
  }
  return { neto: Math.round(neto), impuesto: Math.round(impuesto) };
};

// Ejecuta la vinculación completa. Orden: (1) mov bancario → procesado + FK/ cuota escalar limpias;
// (2) por factura: MBF (monto aplicado) + cuotas tildadas a pagado/cobrado con movimiento_bancario_id
// + factura 'vinculada'; (3) caja consolidada (idempotente por movimiento) + FK caja en el banco.
// Idempotente por par (mov, factura) en MBF y por movimiento en caja. No traga errores (10.177).
//
// params:
//   movimiento           — fila de movimientos_bancarios (id, monto, moneda, fecha).
//   facturasSeleccionadas— [{ id, monto_total, monto_neto, monto_iva, folio, moneda,
//                             cliente_id|proveedor_id, razon_social_receptor|emisor,
//                             venta_tipo?, venta_ref_id?, venta_ref_index?,
//                             cuotasSeleccionadas?: [{id, monto_cuota}], montoAsignado? }]
//   esCobro              — true = cobro (emitidas), false = pago (recibidas).
//   pais, usuarioId
//   entidadId            — override de entidad (cliente/proveedor) resuelta en UI; default = la de f[0].
//   descripcionExtra     — sufijo para la glosa de la caja (propina/forzado breadcrumbs).
//   categoriaPagoOverride— override de categoria_pago de la caja (default cobro_factura/pago_proveedor).
export const ejecutarVinculacionFacturasCuotas = async ({
  movimiento,
  facturasSeleccionadas = [],
  esCobro,
  pais,
  usuarioId,
  entidadId = null,
  descripcionExtra = "",
  categoriaPagoOverride = null,
  supabaseClient = client,
}) => {
  if (!movimiento?.id) throw new Error("ejecutarVinculacionFacturasCuotas: movimiento sin id");
  if (facturasSeleccionadas.length === 0) throw new Error("ejecutarVinculacionFacturasCuotas: sin facturas");

  const tablaFactura = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
  const factuTipoMBF = esCobro ? "sii_emitida" : "sii_recibida";
  const campoEntidad = esCobro ? "cliente_id" : "proveedor_id";
  const esSingle = facturasSeleccionadas.length === 1;
  const ahora = new Date().toISOString();
  const entidadResuelta = entidadId != null ? entidadId : facturasSeleccionadas[0][campoEntidad];
  const entidadNombre = esCobro
    ? facturasSeleccionadas[0].razon_social_receptor
    : facturasSeleccionadas[0].razon_social_emisor;

  let cuotasVinculadas = 0;

  // 1. movimientos_bancarios → procesado. cuota escalar SIEMPRE null (el vínculo N cuotas vive en el
  //    lado cuota); match_* limpios. B3 Fase B: ya NO se escribe la FK directa de factura — la
  //    vinculación vive en el puente MBF (insertado en el paso 2) y nadie lee la FK tras Fase A.
  const updateMov = {
    plan_cobro_cuota_id: null,
    plan_pago_cuota_id: null,
    estado: "procesado",
    procesado_por: usuarioId || null,
    procesado_at: ahora,
    match_entidad_tipo: null,
    match_entidad_id: null,
    match_entidad_nombre: null,
    match_factura_tipo: null,
    match_factura_id: null,
    match_confianza: null,
    match_razon: null,
  };
  {
    const { error } = await supabaseClient.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);
    if (error) throw error;
  }

  // 2. Por factura: MBF (monto aplicado) + cuotas tildadas + marca 'vinculada'.
  for (const f of facturasSeleccionadas) {
    const cuotas = cuotasDe(f);
    const montoAplicado = montoAplicadoFactura(f);

    // 2a. MBF idempotente por (mov, tipo, factura).
    const { data: mbfExistente, error: errSel } = await supabaseClient
      .from("movimiento_bancario_facturas")
      .select("id")
      .eq("movimiento_bancario_id", movimiento.id)
      .eq("factura_tipo", factuTipoMBF)
      .eq("factura_id", f.id)
      .maybeSingle();
    if (errSel) throw errSel;
    if (!mbfExistente) {
      const { error } = await supabaseClient.from("movimiento_bancario_facturas").insert({
        movimiento_bancario_id: movimiento.id,
        factura_tipo: factuTipoMBF,
        factura_id: f.id,
        monto: montoAplicado,
      });
      if (error) throw error;
    }

    // 2b. Cuotas tildadas → pagado/cobrado + vínculo autoritativo (movimiento_bancario_id) + fecha real.
    const tablaPlan = esCobro ? "plan_cobros_facturas" : "plan_pagos_facturas";
    const cambiosCuota = esCobro
      ? { estado: "cobrado", fecha_cobro_real: movimiento.fecha, movimiento_bancario_id: movimiento.id }
      : { estado: "pagado", fecha_pago_real: movimiento.fecha, movimiento_bancario_id: movimiento.id };
    for (const c of cuotas) {
      const { error } = await supabaseClient.from(tablaPlan).update(cambiosCuota).eq("id", c.id);
      if (error) throw error;
      cuotasVinculadas += 1;
    }

    // 2c. Marcar factura 'vinculada'. plazopago/facturafechacobro: emitida siempre; recibida solo si
    //     NO tiene cuotas (con cuotas, las fechas viven en el plan) — replica el single histórico.
    const updateFactura = {
      estado_vinculacion: "vinculada",
      vinculado_por: usuarioId || null,
      vinculado_at: ahora,
    };
    if (esCobro) updateFactura.facturafechacobro = movimiento.fecha;
    else if (cuotas.length === 0) updateFactura.plazopago = movimiento.fecha;
    {
      const { error } = await supabaseClient.from(tablaFactura).update(updateFactura).eq("id", f.id);
      if (error) throw error;
    }

    // 2d. Replicar a facturas_sii_vinculaciones (solo emitidas con venta legacy poblada).
    if (esCobro && f.venta_tipo && f.venta_ref_id != null) {
      const { error } = await supabaseClient.from("facturas_sii_vinculaciones").upsert(
        {
          factura_sii_emitida_id: f.id,
          venta_tipo: f.venta_tipo,
          venta_ref_id: f.venta_ref_id,
          venta_ref_index: f.venta_ref_index ?? null,
          vinculado_por: usuarioId || null,
          vinculado_at: ahora,
        },
        { onConflict: "factura_sii_emitida_id,venta_tipo,venta_ref_id" }
      );
      if (error) throw error;
    }
  }

  // 3. Caja consolidada (una fila por movimiento, idempotente por movimiento_bancario_id).
  const { data: cajaExistente, error: errCaja } = await supabaseClient
    .from("movimientos_caja")
    .select("id")
    .eq("movimiento_bancario_id", movimiento.id)
    .maybeSingle();
  if (errCaja) throw errCaja;

  const categoria = categoriaPagoOverride || (esCobro ? "cobro_factura" : "pago_proveedor");
  let movCajaId = cajaExistente?.id || null;

  if (!cajaExistente) {
    const { neto, impuesto } = netoImpuestoConsolidado(facturasSeleccionadas);
    const folios = facturasSeleccionadas.map((f) => f.folio).join(", ");
    const { data: cajaData, error: errIns } = await supabaseClient
      .from("movimientos_caja")
      .insert([
        {
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto: Math.abs(Number(movimiento.monto || 0)),
          monto_neto: neto,
          monto_impuesto: impuesto,
          moneda: movimiento.moneda || monedaLocalDe(pais),
          estado: "confirmado",
          descripcion: `${entidadNombre} — Folios ${folios}${descripcionExtra}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadResuelta,
          entidad_nombre: entidadNombre,
          categoria_pago: categoria,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        },
      ])
      .select();
    if (errIns) throw errIns;
    movCajaId = cajaData?.[0]?.id || null;
    if (movCajaId) {
      const { error } = await supabaseClient
        .from("movimientos_bancarios")
        .update({ movimiento_caja_id: movCajaId })
        .eq("id", movimiento.id);
      if (error) throw error;
    }
  } else if (!esCobro) {
    // Formalización de un pago a cuenta preexistente: la caja ya existía (bucket 'pago_factura'/
    // 'pago_credito' → badge A). Reclasificar a 'pago_proveedor' para que el badge pase de A a C.
    const { error } = await supabaseClient
      .from("movimientos_caja")
      .update({ categoria_pago: categoria })
      .eq("id", cajaExistente.id);
    if (error) throw error;
  }

  return {
    movCajaId,
    facturasVinculadas: facturasSeleccionadas.length,
    cuotasVinculadas,
    fkEscalar: esSingle,
  };
};
